import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../dash.css";
import { apiService } from "../service/apiService";

// Fix default Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

/* ============ Helpers ============ */
const fmtINR = (n) =>
  n == null
    ? "—"
    : "INR " + new Intl.NumberFormat("en-IN").format(Math.round(n));

const fmtNum = (n) =>
  n == null ? "—" : new Intl.NumberFormat("en-IN").format(n);

const colorClasses = {
  warning: "text-warning",
  primary: "text-primary",
  success: "text-success",
  info: "text-info",
  danger: "text-danger",
};

/* ============ Auto-fit map to markers ============ */
const FitToMarkers = ({ markers }) => {
  const map = useMap();
  useEffect(() => {
    if (!markers || markers.length === 0) return;
    const bounds = L.latLngBounds(markers.map((m) => [m.lat, m.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 9 });
  }, [markers, map]);
  return null;
};

/* ============ StatCard ============ */
const StatCard = ({ title, value, unit, note, color = "primary", icon, showDetails, untracked }) => (
  <div className="col mb-3">
    <div className="card statistic-card h-100 border-0 shadow-sm hover-lift">
      <div className="card-body position-relative">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h6 className="card-title text-muted mb-0 small fw-semibold text-uppercase">
            {title}
          </h6>
          {icon && (
            <span className="fs-5 borderv1">
              <i className={icon} style={{ padding: "8px" }}></i>
            </span>
          )}
        </div>
        <div>
          <div className={`display-2 fs-2 fw-bold font-sans-serif ${colorClasses[color]}`}>
            {value}
          </div>
          {unit && <small className="text-muted">Unit value: {unit}</small>}
          {note && (
            <div>
              <small className="text-muted">{note}</small>
            </div>
          )}
          {untracked && (
            <div>
              <small className="text-muted fst-italic">Not tracked yet</small>
            </div>
          )}
        </div>
      </div>
      <div className="card-footer bg-transparent border-0 pt-0">
        <div className="progress" style={{ height: "4px" }}>
          <div
            className={`progress-bar bg-${color}`}
            style={{ width: "75%" }}
          ></div>
        </div>
      </div>
    </div>
  </div>
);

/* ============ SectionCard ============ */
const SectionCard = ({ title, icon, badge, children }) => (
  <div className="row mb-3">
    <div className="col-md-12">
      <div className="card border-primary">
        <div className="card-header bg-primary text-white p-2 rounded-top d-flex align-items-center">
          <div className="d-flex align-items-center flex-grow-1">
            <span className="fa-stack me-2">
              <i className="fas fa-circle fa-stack-2x text-300"></i>
              <i className={`fa-inverse fa-stack-1x text-primary ${icon}`}></i>
            </span>
            <h6 className="mb-0 fs-0 text-white">{title}</h6>
          </div>
          {badge && (
            <span className="badge bg-warning text-dark ms-2">{badge}</span>
          )}
        </div>
        <div className="card-body">{children}</div>
      </div>
    </div>
  </div>
);

/* ============================================================
   MAIN
   ============================================================ */
const DashboardV3 = () => {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [kitchen, setKitchen] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const loadData = async (filters) => {
    setLoading(true);
    try {
      const payload = {
        kitchen_id: filters.kitchen ?? "",
        start_data: filters.from ?? "",
        end_data: filters.to ?? "",
      };
      const res = await apiService.getDashboardV3Data(payload);
      if (res && res.status) setData(res);
      else {
        console.error("Dashboard API error:", res?.errors || res);
        setData(null);
      }
    } catch (err) {
      console.error(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData({ from: "", to: "", kitchen: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    loadData({ from: fromDate, to: toDate, kitchen });
  };

  if (loading && !data) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "80vh" }}
      >
        <div className="spinner-border text-primary"></div>
      </div>
    );
  }

  const kitchens = data?.kitchens ?? [];
  const inputExpense = data?.inputExpense ?? [];
  const outputUC = data?.outputUnitCost ?? [];
  const mgmtLearning = data?.managementLearning ?? [];
  const kitchenLocs = data?.kitchenLocations ?? [];

  const expenseIcons = [
    "fas fa-cubes",
    "fas fa-users",
    "fas fa-truck",
    "fas fa-tools",
  ];
  const mealIcons = [
    "fas fa-mug-hot",
    "fas fa-utensils",
    "fas fa-drumstick-bite",
  ];

  return (
    <div>
      <div className="content">
        <div className="container-fluid">

          {/* ===== FILTER ===== */}
          <div className="card mb-3">
            <div className="card-header border-info">
              <form onSubmit={(e) => e.preventDefault()}>
                <div className="row g-3">
                  <div className="col-lg-3">
                    <label className="form-label small mb-1">From Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div className="col-lg-3">
                    <label className="form-label small mb-1">To Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                  <div className="col-lg-4">
                    <label className="form-label small mb-1">Kitchen</label>
                    <select
                      className="form-select"
                      value={kitchen}
                      onChange={(e) => setKitchen(e.target.value)}
                    >
                      <option value="">All Kitchens</option>
                      {kitchens.map((k) => (
                        <option key={k.kitchen_id} value={k.kitchen_id}>
                          {k.kitchen_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-lg-2 d-flex align-items-end">
                    <button
                      type="button"
                      className="btn btn-primary w-100"
                      onClick={handleSearch}
                      disabled={loading}
                    >
                      {loading ? "Loading…" : "GO"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* ===== INPUT & EXPENSE ===== */}
          <SectionCard title="Input and Expense" icon="fas fa-arrow-down">
            <div className="row g-3">
              {inputExpense.map((c, idx) => (
                <StatCard
                  key={idx}
                  title={c.title}
                  value={c.tracked ? fmtINR(c.value) : "—"}
                  color="warning"
                  icon={expenseIcons[idx] || "fas fa-cubes"}
                  showDetails={idx === 0}
                  untracked={!c.tracked}
                />
              ))}
            </div>
          </SectionCard>

          {/* ===== OUTPUT & UNIT COST ===== */}
          <SectionCard title="Output and Unit Cost" icon="fas fa-arrow-up">
            <div className="row g-3">
              {outputUC.map((c, idx) => (
                <StatCard
                  key={idx}
                  title={c.title}
                  value={c.value == null ? "—" : fmtNum(c.value)}
                  unit={c.unit}
                  color="success"
                  icon={mealIcons[idx] || "fas fa-utensils"}
                  untracked={!c.tracked}
                />
              ))}
            </div>
          </SectionCard>

          {/* ===== MANAGEMENT LEARNING ===== */}
          <SectionCard title="Management Learning" icon="fas fa-graduation-cap">
            <div className="row g-3">
              {mgmtLearning.map((c, idx) => (
                <StatCard
                  key={idx}
                  title={c.title}
                  value={c.value == null ? "—" : c.value}
                  note={c.note}
                  color="info"
                  icon="fas fa-project-diagram"
                  untracked={!c.tracked}
                />
              ))}
            </div>
          </SectionCard>

          {/* ===== MAP ===== */}
          <SectionCard
            title="Map"
            icon="fas fa-map-marker-alt"
            badge={(() => {
              const mapped = kitchenLocs.filter((k) => k.lat != null).length;
              const total = kitchenLocs.length;
              return total ? `${mapped}/${total} mapped` : null;
            })()}
          >
            <div style={{ borderRadius: 8, overflow: "hidden" }}>
              <MapContainer
                center={[22.9734, 78.6569]}
                zoom={5}
                scrollWheelZoom={false}
                style={{ height: "450px", width: "100%" }}
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitToMarkers
                  markers={kitchenLocs.filter(
                    (k) => k.lat != null && k.lng != null
                  )}
                />
                {kitchenLocs
                  .filter((k) => k.lat != null && k.lng != null)
                  .map((k) => (
                    <Marker key={k.id} position={[k.lat, k.lng]}>
                      <Popup>
                        <strong>{k.name}</strong>
                        <br />
                        <small className="text-muted">{k.location}</small>
                      </Popup>
                    </Marker>
                  ))}
              </MapContainer>
            </div>
          </SectionCard>

        </div>
      </div>
    </div>
  );
};

export default DashboardV3;
